import { useEffect, useLayoutEffect, useState, type JSX } from 'react'
import { useSubmit } from 'react-router'
import { client, type Client, hostedFields, type HostedFieldFieldOptions } from 'braintree-web'
import {
  Box,
  Checkbox,
  FormControlLabel,
  styled,
  SubmitButton,
  TextField,
  useTheme,
  type BoxProps,
  type CheckboxProps,
  type SubmitButtonProps,
  type TextFieldProps,
  type Theme,
} from '@trinity/components'
import { INTENTS } from '@trinity/utils'

interface UseHostedFieldsProps {
  clientToken: string
  creditCard?: TrinityAPI.CreditCardType
}

export function useHostedFields({ clientToken, creditCard }: UseHostedFieldsProps): ReturnType<
  typeof getFormComponents
> & {
  SubmitButton: (props: Omit<SubmitButtonProps, 'isLoading' | 'disabled' | 'id'>) => JSX.Element
} {
  const theme = useTheme()
  const submit = useSubmit()
  const [clientInstance, setClientInstance] = useState<Client | undefined>()
  const [nonce, setNonce] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (nonce) {
      const checkbox = document.getElementById('isPrimary') as HTMLInputElement | undefined
      const isPrimary = checkbox?.checked ?? 'false'
      const token = creditCard?.token ?? null

      void submit({ intent: INTENTS.ADD_CARD, nonce, default: isPrimary, token }, { method: 'POST' })
    }
  }, [creditCard?.token, nonce, submit])

  useEffect(() => {
    if (!clientInstance) {
      client.create({ authorization: clientToken }, (err, clientInstance: Client) => {
        if (err) {
          console.error(err)
          return
        }
        setClientInstance(clientInstance)
      })
    }
  }, [clientInstance, clientToken])

  useLayoutEffect(() => {
    if (clientInstance && !isSubmitting) {
      hostedFields.create(
        {
          client: clientInstance,
          styles: getFormStyles(theme),
          fields: getFields(creditCard),
        },
        (err, hostedFieldsInstance) => {
          if (err) {
            console.error(err)
            return
          }
          if (!hostedFieldsInstance) throw new Error('Hosted Fields instance is undefined')

          const button = document.getElementById('submit-button')

          hostedFieldsInstance.on('validityChange', () => {
            const state = hostedFieldsInstance.getState()
            const formValid = Object.keys(state.fields).every(
              key => state.fields[key as keyof HostedFieldFieldOptions].isValid,
            )

            if (!formValid) {
              button?.setAttribute('disabled', 'true')
              button?.classList.add('Mui-disabled')
            } else {
              button?.removeAttribute('disabled')
              button?.classList.remove('Mui-disabled')
            }
          })

          button?.addEventListener('click', () => {
            hostedFieldsInstance.tokenize((tokenizeError, payload) => {
              if (tokenizeError) console.error(tokenizeError)
              if (!payload?.nonce) throw new Error('No token was returned from Braintree - please try again')

              button?.setAttribute('disabled', 'true')
              button?.classList.add('Mui-disabled')
              setIsSubmitting(true)
              setNonce(payload.nonce)
            })
          })
        },
      )
    }
  }, [clientInstance, creditCard, isSubmitting, theme])

  return {
    ...getFormComponents(creditCard),
    SubmitButton: (props: Omit<SubmitButtonProps, 'isLoading' | 'disabled' | 'id'>) => (
      <SubmitButton disabled id='submit-button' isLoading={isSubmitting} {...props}>
        Save
      </SubmitButton>
    ),
  }
}

//* HELPERS
const getFormStyles = (theme: Theme) => {
  //! cts; 2026-09-28; I do not know why, but typescript suddenly had issues
  //! with these in the hostedField object above.
  const fontFamily = theme.typography.body1.fontFamily?.toString() ?? 'sans-serif'
  const fontSize = theme.typography.body1.fontSize?.toString() ?? '16'
  const fontWeight = theme.typography.body1.fontWeight?.toString() ?? '500'
  const errorColor = theme.palette.error.main ?? '#D63737'
  const successColor = theme.palette.success.main ?? '#17A95D'
  const bodyColor = theme.typography.body1.color ?? '#9B9EB8'

  return {
    input: {
      'font-family': fontFamily,
      'font-size': fontSize,
      'font-weight': fontWeight,
      'margin-left': '10px',
    },
    'input.invalid': {
      color: errorColor,
      border: `1px solid ${errorColor}`,
    },
    'input.valid': {
      color: successColor,
    },
    select: {
      'font-family': fontFamily,
      'font-size': fontSize,
      'font-weight': fontWeight,
      color: bodyColor,
    },
  }
}

const getFields = (creditCard?: TrinityAPI.CreditCardType) => {
  // If credit card is passed, then we are editing the existing card
  if (creditCard) {
    const { number: _number, cardholderName: _cardholderName, ...rest } = fields
    const formFields = { ...rest }
    formFields.expirationMonth.prefill = creditCard.expirationMonth
    formFields.expirationYear.prefill = creditCard.expirationYear
    formFields.postalCode.prefill = creditCard.zipCode ?? ''
    return formFields
  }

  // If no credit card is passed, then we are creating a new card
  return { ...fields }
}

const getFormComponents = (
  creditCard?: TrinityAPI.CreditCardType,
): {
  CVV: ReturnType<typeof FormElement>
  ExpirationMonth: ReturnType<typeof FormElement>
  ExpirationYear: ReturnType<typeof FormElement>
  CreditCardNumber: ReturnType<typeof FormElement> | ReturnType<typeof DisabledForm>
  CardholderName: ReturnType<typeof FormElement> | ReturnType<typeof DisabledForm>
  ZipCode: ReturnType<typeof FormElement>
  PrimaryCheckbox: typeof PrimaryCheckbox
} => {
  const CVV = FormElement(fields.cvv.container.slice(1))
  const ExpirationMonth = FormElement(fields.expirationMonth.container.slice(1))
  const ExpirationYear = FormElement(fields.expirationYear.container.slice(1))
  const ZipCode = FormElement(fields.postalCode.container.slice(1))
  const CreditCardNumber = creditCard
    ? DisabledForm(`•••• •••• ••••  ${creditCard.last4}`)
    : FormElement(fields.number.container.slice(1))
  const CardholderName = creditCard
    ? DisabledForm(creditCard.cardholderName)
    : FormElement(fields.cardholderName.container.slice(1))

  return {
    CVV,
    ExpirationMonth,
    ExpirationYear,
    CreditCardNumber,
    CardholderName,
    ZipCode,
    PrimaryCheckbox,
  }
}

//* FIELDS
const fields = {
  number: {
    container: '#card-number',
    placeholder: '•••• •••• •••• ••••',
    maskInput: { showLastFour: true },
    prefill: '',
  },
  cvv: {
    container: '#cvv',
    placeholder: '3-4 digit code',
    maskInput: true,
    prefill: '',
  },
  expirationMonth: {
    container: '#expiration-month',
    placeholder: 'MM',
    prefill: '',
    select: {
      options: [
        'JAN - 01',
        'FEB - 02',
        'MAR - 03',
        'APR - 04',
        'MAY - 05',
        'JUN - 06',
        'JUL - 07',
        'AUG - 08',
        'SEP - 09',
        'OCT - 10',
        'NOV - 11',
        'DEC - 12',
      ],
    },
  },
  expirationYear: {
    container: '#expiration-year',
    placeholder: 'YYYY',
    select: true,
    prefill: '',
  },
  cardholderName: {
    container: '#cardholder-name',
    placeholder: 'First and last name',
    prefill: '',
  },
  postalCode: {
    container: '#postal-code',
    placeholder: 'Zip Code',
    prefill: '',
  },
}

//* STYLED COMPONENTS
const FormElement = (id: string): React.FC<BoxProps> =>
  styled((props: BoxProps) => <Box id={id} {...props} />)(({ theme }) => ({
    width: '100%',
    height: 50,
    border: theme.borders.n100,
    '&.braintree-hosted-fields-invalid': {
      border: theme.borders.error,
    },
    '&.braintree-hosted-fields-valid': {
      border: theme.borders.success,
    },
  }))

const DisabledForm: (value: string) => React.FC = (value: string) =>
  styled((props: TextFieldProps) => <TextField disabled value={value} {...props} />)(() => null)

const PrimaryCheckbox = (props: CheckboxProps) => (
  <FormControlLabel
    name='isPrimary'
    control={<Checkbox id='isPrimary' {...props} />}
    label='Make This The Primary Card On My Account'
  />
)
